import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Truck, Shield, Star, ArrowRight, Package, Tag, ShoppingCart, Gift, Copy, Store, UserPlus } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  { key: 'sand', label: 'Sand', emoji: '🏖️', color: '#F59E0B', desc: 'River, M-sand' },
  { key: 'cement', label: 'Cement', emoji: '🧱', color: '#6B7280', desc: 'OPC, PPC' },
  { key: 'iron', label: 'Iron/Steel', emoji: '⚙️', color: '#374151', desc: 'TMT bars, rods' },
  { key: 'bricks', label: 'Bricks', emoji: '🔶', color: '#DC2626', desc: 'Standard, fly ash' },
  { key: 'long_bricks', label: 'Long Bricks', emoji: '🟧', color: '#EA580C', desc: 'Hollow, solid' },
  { key: 'short_bricks', label: 'Short Bricks', emoji: '🟫', color: '#92400E', desc: 'Paving, interlocking' },
];

const OFFERS = [
  { title: '15% Off River Sand', subtitle: 'Bulk orders this season', tag: 'sand', color: '#F59E0B', bg: '#FFFBEB' },
  { title: 'Buy 1000, Get 50 Free Bricks', subtitle: 'Direct from kiln', tag: 'bricks', color: '#DC2626', bg: '#FEF2F2' },
  { title: 'TMT Steel – Best Price', subtitle: 'Fe500D, direct dealer', tag: 'iron', color: '#374151', bg: '#F9FAFB' },
];

const DEFAULT_HOME_BRANDS = [
  { name: 'ACC', text: 'Reliable cement for slabs, columns, and masonry', link: '/shops?search=ACC' },
  { name: 'Ambuja', text: 'Popular PPC cement from verified nearby suppliers', link: '/shops?search=Ambuja' },
  { name: 'TATA Steel', text: 'TMT steel and rods for strong construction', link: '/shops?search=TATA' },
  { name: 'JSW Steel', text: 'Quality steel materials with local delivery', link: '/shops?search=JSW' },
];

export default function Home() {
  const [search, setSearch] = useState('');
  const [shops, setShops] = useState([]);
  const [referral, setReferral] = useState({ enabled: true, customerReward: 20, ownerReward: 50 });
  const [advanceAmount, setAdvanceAmount] = useState(499);
  const [banners, setBanners] = useState([]);
  const [homeBrands, setHomeBrands] = useState([]);
  const [homeCategories, setHomeCategories] = useState([]);
  const [popularMaterials, setPopularMaterials] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { API.get('/owners').then(r => setShops(r.data.slice(0, 3))).catch(() => {}); }, []);
  useEffect(() => {
    API.get('/admin/public-settings')
      .then(r => {
        setReferral(r.data.referral || referral);
        setAdvanceAmount(Number(r.data.payment?.advanceAmount) || 499);
        setBanners(r.data.banners || []);
        setHomeBrands(r.data.homeBrands || []);
        setHomeCategories(r.data.homeCategories || []);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    API.get('/materials')
      .then(r => setPopularMaterials((r.data || []).slice(0, 6)))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => { e.preventDefault(); navigate(`/shops?search=${encodeURIComponent(search)}`); };
  const customerReferralLink = `${window.location.origin}/register?ref=${user?.referralCode || ''}`;
  const ownerReferralLink = `${window.location.origin}/register/owner?ref=${user?.referralCode || ''}`;
  const displayCategories = CATEGORIES.map(cat => ({
    ...cat,
    ...(homeCategories.find(item => item.key === cat.key) || {})
  })).filter(cat => cat.active !== false);
  const copyLink = async (link) => {
    await navigator.clipboard.writeText(link);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }} className="page-with-bottom-nav">
      <Navbar />

      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-inner">
          <div className="home-hero-content">
            <div className="home-hero-eyebrow">
              <Shield size={14} /> Verified local supply network
            </div>
            <h1>Construction materials, <span>delivered near you.</span></h1>
            <p>
              Compare sand, cement, steel and bricks from approved suppliers. Pay Rs {advanceAmount} to confirm and settle the balance on delivery.
            </p>

            <form className="home-hero-search" onSubmit={handleSearch}>
              <MapPin size={19} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="What material do you need?"
                aria-label="Search materials, shops or locations"
              />
              <button type="submit"><Search size={17} /> Search</button>
            </form>

            <div className="home-hero-shortcuts" aria-label="Popular searches">
              {['Sand', 'Cement', 'TMT steel', 'Bricks'].map(item => (
                <button key={item} type="button" onClick={() => navigate(`/shops?view=materials&search=${encodeURIComponent(item)}`)}>
                  {item}<ArrowRight size={12} />
                </button>
              ))}
            </div>

            <div className="home-hero-proof">
              <div><Shield size={18} /><span><strong>Verified sellers</strong><small>Documents reviewed</small></span></div>
              <div><Truck size={18} /><span><strong>Local delivery</strong><small>Direct from suppliers</small></span></div>
              <div><Star size={18} /><span><strong>4.8 average</strong><small>Trusted marketplace</small></span></div>
            </div>
          </div>
        </div>
      </section>

      {banners.length > 0 && (
        <section style={{ padding: '18px 16px', background: 'white' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12 }}>
            {banners.map((banner, index) => (
              <Link key={index} to={banner.link || '/shops'} style={{ textDecoration: 'none', color: 'white', background: 'linear-gradient(135deg,var(--orange),#1A1A1A)', borderRadius: 14, padding: 18, minHeight: 92, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontWeight: 900, fontSize: 17, marginBottom: 4 }}>{banner.title}</div>
                <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: 13 }}>{banner.subtitle}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Brands */}
      <section style={{ padding: '34px 16px', background: 'white' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 19, fontWeight: 700 }}><Shield size={18} color="var(--orange)" /> Trusted Brands</h2>
            <Link to="/shops" style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--orange)', fontSize: 13, fontWeight: 600 }}>View Materials <ArrowRight size={13} /></Link>
          </div>
          <div className="home-brand-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            {(homeBrands.length ? homeBrands : DEFAULT_HOME_BRANDS).slice(0, 8).map((brand, index) => (
              <Link key={index} to={brand.link || `/shops?search=${encodeURIComponent(brand.name || '')}`} style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-border)', borderRadius: 14, padding: 14, color: 'var(--dark)', display: 'flex', alignItems: 'center', gap: 12, minHeight: 96 }}>
                <div style={{ width: 52, height: 52, borderRadius: 13, background: 'white', border: '1px solid var(--gray-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {brand.image ? (
                    <img src={brand.image} alt={brand.name || 'Brand'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: 'var(--orange)', fontWeight: 900, fontSize: 15 }}>{String(brand.name || 'B').slice(0, 2).toUpperCase()}</span>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, fontFamily: 'var(--font-main)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brand.name}</div>
                  <div style={{ color: 'var(--gray)', fontSize: 11, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{brand.text}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Materials */}
      {popularMaterials.length > 0 && (
        <section className="home-popular-section" style={{ padding: '36px 16px' }}>
          <div className="home-popular-inner" style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div className="home-popular-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
              <div>
                <span>Trending near you</span>
                <h2><Package size={18} /> Popular Materials</h2>
                <p>Frequently ordered products from verified local suppliers.</p>
              </div>
              <Link to="/shops?view=materials">Browse all <ArrowRight size={14} /></Link>
            </div>
            <div className="home-material-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {popularMaterials.map(mat => (
                <Link className="home-material-card" key={mat._id} to={`/materials/${mat._id}`}>
                  <div className="home-material-image">
                    {mat.images?.[0] ? <img src={mat.images[0]} alt={mat.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Package size={24} color="var(--gray)" />}
                    <span>{mat.brand || mat.category}</span>
                  </div>
                  <div className="home-material-body">
                    <div className="home-material-name-row">
                      <h3>{mat.name}</h3>
                      <ArrowRight size={15} />
                    </div>
                    <div className="home-material-unit">Per {mat.unit || 'unit'}</div>
                    <div className="home-material-price-row">
                      <strong>Rs {Number(mat.price || 0).toLocaleString('en-IN')}</strong>
                      <span className={Number(mat.stock || 0) > 0 ? 'in-stock' : 'out-stock'}>{Number(mat.stock || 0) > 0 ? 'In stock' : 'Out of stock'}</span>
                    </div>
                    <div className="home-material-supplier">
                      <Store size={13} />
                      <span>{mat.owner?.shopName || 'Verified supplier'}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Offers */}
      <section style={{ padding: '40px 16px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 19, fontWeight: 700 }}><Tag size={18} color="var(--orange)" /> Active Offers</h2>
            <Link to="/shops" style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--orange)', fontSize: 13, fontWeight: 600 }}>See All <ArrowRight size={13} /></Link>
          </div>
          <div className="offers-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            {OFFERS.map((o, i) => (
              <div key={i} style={{ background: o.bg, borderRadius: 12, padding: 18, borderLeft: `4px solid ${o.color}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ background: o.color + '25', color: o.color, padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, width: 'fit-content', textTransform: 'uppercase' }}>{o.tag}</div>
                <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-main)' }}>{o.title}</div>
                <div style={{ color: 'var(--gray)', fontSize: 12 }}>{o.subtitle}</div>
                <Link to={`/shops?search=${o.tag}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: o.color, fontSize: 12, fontWeight: 600, marginTop: 4 }}>Shop Now <ArrowRight size={12} /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section style={{ padding: '40px 16px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 19, fontWeight: 700, marginBottom: 18 }}><Package size={18} color="var(--orange)" /> Shop by Category</h2>
          <div className="cat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 12 }}>
            {displayCategories.map(cat => (
              <Link key={cat.key} to={`/shops?category=${cat.key}`} style={{ background: 'white', borderRadius: 14, padding: '16px 10px', textAlign: 'center', textDecoration: 'none', color: 'var(--dark)', boxShadow: 'var(--shadow)', transition: 'all 0.2s' }}>
                <div style={{ width: 48, height: 48, background: cat.color + '15', borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                  {cat.image ? (
                    <img src={cat.image} alt={cat.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Package size={21} color={cat.color} />
                  )}
                </div>
                <div style={{ fontWeight: 700, fontSize: 12, fontFamily: 'var(--font-main)', marginBottom: 2 }}>{cat.label}</div>
                <div style={{ color: 'var(--gray)', fontSize: 10 }}>{cat.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Shops */}
      {shops.length > 0 && (
        <section style={{ padding: '10px 16px 40px', background: 'white' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 19, fontWeight: 700 }}><MapPin size={18} color="var(--orange)" /> Nearby Shops</h2>
              <Link to="/shops" style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--orange)', fontSize: 13, fontWeight: 600 }}>View All <ArrowRight size={13} /></Link>
            </div>
            <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              {shops.map(shop => (
                <Link key={shop._id} to={`/shops/${shop._id}`} style={{ background: 'var(--gray-light)', borderRadius: 14, padding: 16, textDecoration: 'none', color: 'var(--dark)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 44, height: 44, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>{shop.shopName?.[0] || 'S'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shop.shopName}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--gray)', fontSize: 12 }}><MapPin size={10} /> {shop.location?.city}</div>
                    </div>
                    <span className="badge badge-green" style={{ fontSize: 10 }}>Open</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <Star size={12} color="#F59E0B" fill="#F59E0B" />
                    <span style={{ fontWeight: 600 }}>{shop.rating?.toFixed(1) || '4.5'}</span>
                    <span style={{ color: 'var(--gray)', fontSize: 12 }}>· {shop.location?.address}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section style={{ padding: '50px 16px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, marginBottom: 32 }}>How Material Stock Works</h2>
          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
            {[
              { icon: Search, step: '01', title: 'Search & Discover', desc: 'Find verified shops by location, material or name' },
              { icon: ShoppingCart, step: '02', title: 'Add to Cart', desc: 'Select materials, sizes and quantities' },
              { icon: Shield, step: '03', title: `Pay ₹${advanceAmount} Advance`, desc: 'Confirm with small advance via Razorpay' },
              { icon: Truck, step: '04', title: 'Get Delivered', desc: 'Pay remaining cash or online on delivery' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: 22, borderRadius: 14, background: 'white', boxShadow: 'var(--shadow)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 10, right: 12, color: 'var(--orange)', fontSize: 30, fontWeight: 800, opacity: 0.1 }}>{s.step}</div>
                <div style={{ width: 50, height: 50, background: 'var(--orange-soft)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <s.icon size={22} color="var(--orange)" />
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-main)', marginBottom: 6 }}>{s.title}</div>
                <div style={{ color: 'var(--gray)', fontSize: 12, lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg,var(--orange) 0%,var(--amber) 100%)', padding: '50px 16px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <h2 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 10 }}>Are you a Material Supplier?</h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, marginBottom: 24 }}>Register your shop, upload your documents, and start receiving orders from customers near you.</p>
          <Link to="/register/owner" style={{ background: 'white', color: 'var(--orange)', padding: '13px 28px', borderRadius: 10, fontWeight: 700, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Register as Supplier <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '50px 16px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
            {[
              { icon: Shield, title: 'Verified Owners', desc: 'All suppliers verified with Aadhar & documents' },
              { icon: MapPin, title: 'Nearby Delivery', desc: 'Location-based shop discovery like Zepto' },
              { icon: Truck, title: 'Fast Delivery', desc: 'Sand loads, bulk bricks, iron rods delivered fast' },
              { icon: Star, title: 'Best Prices', desc: 'Competitive pricing with seasonal offers' },
            ].map((f, i) => (
              <div key={i} style={{ padding: 20, borderRadius: 14, border: '1px solid var(--gray-border)', background: 'white' }}>
                <div style={{ width: 44, height: 44, background: 'var(--orange-soft)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <f.icon size={20} color="var(--orange)" />
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-main)', marginBottom: 5 }}>{f.title}</div>
                <div style={{ color: 'var(--gray)', fontSize: 12, lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Program */}
      {referral.enabled !== false && (
        <section style={{ padding: '50px 16px', background: 'white' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ background: 'linear-gradient(135deg,#1A1A1A,#2D2D2D)', borderRadius: 18, padding: '28px 24px', color: 'white', display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 24, alignItems: 'center' }} className="grid-2">
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--orange-soft)', color: 'var(--orange)', padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
                  <Gift size={13} /> Referral Program
                </div>
                <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 10 }}>Invite customers and suppliers</h2>
                <p style={{ color: '#D1D5DB', fontSize: 14, lineHeight: 1.7, maxWidth: 520 }}>
                  Share your referral code with people ordering materials or suppliers joining Material Stock. Rewards are added to your wallet when they register.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
                <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 16 }}>
                  <UserPlus size={20} color="var(--orange)" />
                  <div style={{ fontWeight: 800, fontSize: 22, marginTop: 8 }}>₹{referral.customerReward}</div>
                  <div style={{ color: '#D1D5DB', fontSize: 12 }}>for each customer signup</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 16 }}>
                  <Store size={20} color="var(--orange)" />
                  <div style={{ fontWeight: 800, fontSize: 22, marginTop: 8 }}>₹{referral.ownerReward}</div>
                  <div style={{ color: '#D1D5DB', fontSize: 12 }}>for each owner signup</div>
                </div>
                {user?.referralCode ? (
                  <div style={{ gridColumn: '1 / -1', background: 'white', color: 'var(--dark)', borderRadius: 14, padding: 14 }}>
                    <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 800, marginBottom: 4 }}>YOUR REFERRAL CODE</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 900, fontSize: 20, letterSpacing: 1 }}>{user.referralCode}</span>
                      <button onClick={() => copyLink(customerReferralLink)} className="btn-primary" style={{ padding: '8px 10px', fontSize: 12 }}>
                        <Copy size={13} /> Customer Link
                      </button>
                      <button onClick={() => copyLink(ownerReferralLink)} className="btn-outline" style={{ padding: '6px 10px', fontSize: 12 }}>
                        <Copy size={13} /> Owner Link
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link to="/register" className="btn-primary" style={{ gridColumn: '1 / -1', justifyContent: 'center', padding: '12px' }}>
                    Get Referral Code <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      {false && <footer style={{ background: 'var(--dark)', padding: '28px 16px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="footer-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Package size={18} color="var(--orange)" />
              <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, fontSize: 16, color: 'white' }}>Material Stock</span>
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {[['Find Shops', '/shops'], ['Sign Up', '/register'], ['Wishlist', '/wishlist'], ['Sell on Material Stock', '/register/owner']].map(([l, h]) => (
                <Link key={l} to={h} style={{ color: '#9CA3AF', fontSize: 13, textDecoration: 'none' }}>{l}</Link>
              ))}
            </div>
            <div style={{ color: '#6B7280', fontSize: 12 }}>© 2024 Material Stock</div>
          </div>
        </div>
      </footer>}
    </div>
  );
}
